import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useOutlet } from '../../context/OutletContext';

const TerminalContext = createContext();

export const useTerminal = () => {
  const context = useContext(TerminalContext);
  if (!context) throw new Error('useTerminal must be used within TerminalProvider');
  return context;
};

// Terminal Types
export const TERMINAL_TYPES = {
  BILLING: 'billing',
  KOT: 'kot',
  MASTER: 'master',
  CAPTAIN: 'captain'
};

// Terminal Status
export const TERMINAL_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  OFFLINE: 'offline',
  MAINTENANCE: 'maintenance'
};

export const TerminalProvider = ({ children }) => {
  const { selectedOutlet } = useOutlet();
  const [terminals, setTerminals] = useState([]);
  const [currentTerminal, setCurrentTerminal] = useState(null);
  const [terminalOrders, setTerminalOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load terminals for current outlet
  useEffect(() => {
    if (!selectedOutlet) {
      setLoading(false);
      return;
    }

    console.log(`🖥️ Loading terminals for outlet: ${selectedOutlet.name}`);

    const q = query(
      collection(db, 'terminals'),
      where('outletId', '==', selectedOutlet.id),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const terminalsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📊 Loaded ${terminalsData.length} terminals`);
      setTerminals(terminalsData);
      setLoading(false);

      // Auto-select master terminal if available
      const masterTerminal = terminalsData.find(t => t.type === TERMINAL_TYPES.MASTER && t.status === TERMINAL_STATUS.ACTIVE);
      if (masterTerminal && !currentTerminal) {
        setCurrentTerminal(masterTerminal);
        localStorage.setItem('currentTerminalId', masterTerminal.id);
      }
    });

    return () => unsubscribe();
  }, [selectedOutlet]);

  // Load current terminal from localStorage
  useEffect(() => {
    const savedTerminalId = localStorage.getItem('currentTerminalId');
    if (savedTerminalId && terminals.length > 0) {
      const terminal = terminals.find(t => t.id === savedTerminalId);
      if (terminal) {
        setCurrentTerminal(terminal);
      }
    }
  }, [terminals]);

  // Subscribe to orders for current terminal
  useEffect(() => {
    if (!currentTerminal || !selectedOutlet) return;

    console.log(`📋 Loading orders for terminal: ${currentTerminal.name}`);

    const q = query(
      collection(db, 'orders'),
      where('outletId', '==', selectedOutlet.id),
      where('terminalId', '==', currentTerminal.id),
      where('status', 'in', ['pending', 'in_progress', 'prepared']),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📊 Loaded ${ordersData.length} orders for terminal ${currentTerminal.name}`);
      setTerminalOrders(ordersData);
    });

    return () => unsubscribe();
  }, [currentTerminal, selectedOutlet]);

  // Create new terminal
  const createTerminal = async (terminalData) => {
    try {
      if (!selectedOutlet) {
        return { success: false, error: 'No outlet selected' };
      }

      // Generate terminal number
      const terminalNumber = `T${(terminals.length + 1).toString().padStart(3, '0')}`;

      const newTerminal = {
        ...terminalData,
        terminalNumber,
        outletId: selectedOutlet.id,
        outletName: selectedOutlet.name,
        status: TERMINAL_STATUS.ACTIVE,
        lastActive: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'terminals'), newTerminal);
      console.log(`✅ Terminal created: ${docRef.id}`);

      return { success: true, id: docRef.id, terminalNumber };
    } catch (error) {
      console.error('❌ Error creating terminal:', error);
      return { success: false, error: error.message };
    }
  };

  // Update terminal
  const updateTerminal = async (terminalId, updates) => {
    try {
      await updateDoc(doc(db, 'terminals', terminalId), {
        ...updates,
        updatedAt: serverTimestamp()
      });

      console.log(`✅ Terminal updated: ${terminalId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating terminal:', error);
      return { success: false, error: error.message };
    }
  };

  // Delete terminal
  const deleteTerminal = async (terminalId) => {
    try {
      await deleteDoc(doc(db, 'terminals', terminalId));
      console.log(`✅ Terminal deleted: ${terminalId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting terminal:', error);
      return { success: false, error: error.message };
    }
  };

  // Set current terminal
  const selectTerminal = (terminalId) => {
    const terminal = terminals.find(t => t.id === terminalId);
    if (terminal) {
      setCurrentTerminal(terminal);
      localStorage.setItem('currentTerminalId', terminalId);

      // Update last active time
      updateTerminal(terminalId, {
        lastActive: serverTimestamp()
      });

      console.log(`🖥️ Switched to terminal: ${terminal.name}`);
    }
  };

  // Update terminal status
  const updateTerminalStatus = async (terminalId, status) => {
    return await updateTerminal(terminalId, { status });
  };

  // Heartbeat - update terminal as active
  const heartbeat = async () => {
    if (currentTerminal) {
      await updateTerminal(currentTerminal.id, {
        lastActive: serverTimestamp(),
        status: TERMINAL_STATUS.ACTIVE
      });
    }
  };

  // Start heartbeat on mount
  useEffect(() => {
    const interval = setInterval(() => {
      heartbeat();
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [currentTerminal]);

  // Get active terminals
  const getActiveTerminals = () => {
    return terminals.filter(t => t.status === TERMINAL_STATUS.ACTIVE);
  };

  // Get terminals by type
  const getTerminalsByType = (type) => {
    return terminals.filter(t => t.type === type);
  };

  // Get master terminal
  const getMasterTerminal = () => {
    return terminals.find(t => t.type === TERMINAL_TYPES.MASTER);
  };

  // Sync order to all terminals (for master station)
  const syncOrderToTerminals = async (orderId, orderData) => {
    try {
      // This will automatically sync via Firestore real-time listeners
      // Just ensure the order has the outletId
      console.log(`📡 Order ${orderId} synced to all terminals`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error syncing order:', error);
      return { success: false, error: error.message };
    }
  };

  // Generate token number for the day
  const generateTokenNumber = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get count of orders today for this outlet
      const ordersToday = terminalOrders.filter(order => {
        const orderDate = order.timestamp?.toDate ? order.timestamp.toDate() : new Date();
        orderDate.setHours(0, 0, 0, 0);
        return orderDate.getTime() === today.getTime();
      });

      const tokenNumber = ordersToday.length + 1;
      return tokenNumber;
    } catch (error) {
      console.error('❌ Error generating token number:', error);
      return Math.floor(Math.random() * 1000) + 1;
    }
  };

  const value = {
    terminals,
    currentTerminal,
    terminalOrders,
    loading,
    createTerminal,
    updateTerminal,
    deleteTerminal,
    selectTerminal,
    updateTerminalStatus,
    getActiveTerminals,
    getTerminalsByType,
    getMasterTerminal,
    syncOrderToTerminals,
    generateTokenNumber,
    TERMINAL_TYPES,
    TERMINAL_STATUS
  };

  return (
    <TerminalContext.Provider value={value}>
      {children}
    </TerminalContext.Provider>
  );
};
